import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import {
  clearCompanyActivationInterestDraft,
  getCompanyActivationInterest,
  getCompanyActivationInterestDraft,
  saveCompanyActivationInterest,
  saveCompanyActivationInterestDraft,
} from '@/lib/company/activation-interest';
import { COMPANY_CLAIM_URL } from '@/lib/company/claim';
import { buildGoogleMapsUrl } from '@/lib/company/google-maps';
import { resolveCompanyBanner } from '@/lib/image-placeholders';
import { formatPhoneNumber, isPhoneNumberComplete } from '@/lib/phoneNumber';
import type { ApiCompanyBySlug } from '@/types/api';
import { Bell, BellRing, CheckCircle2, ExternalLink, MapPin, Phone, Store } from 'lucide-react';
import { useRef, useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa';
import { toast } from 'sonner';
import { useMaskInput } from 'use-mask-input';

interface CompanyInactiveStateProps {
  company: ApiCompanyBySlug;
  fullAddress: string;
  whatsappUrl: string | null;
}

interface InterestViewState {
  hasSavedInterest: boolean;
  interestName: string;
  interestPhoneNumber: string;
  isInterestExpanded: boolean;
}

function buildInterestViewState(
  companyId: string,
  user?: {
    name?: string | null;
    phoneNumber?: string | null;
  } | null,
): InterestViewState {
  const savedInterest = getCompanyActivationInterest(companyId);

  if (savedInterest) {
    return {
      hasSavedInterest: true,
      interestName: savedInterest.customerName,
      interestPhoneNumber: savedInterest.customerPhoneNumber,
      isInterestExpanded: true,
    };
  }

  const savedDraft = getCompanyActivationInterestDraft(companyId);

  if (savedDraft) {
    return {
      hasSavedInterest: false,
      interestName: savedDraft.customerName,
      interestPhoneNumber: savedDraft.customerPhoneNumber,
      isInterestExpanded: savedDraft.isExpanded,
    };
  }

  return {
    hasSavedInterest: false,
    interestName: user?.name?.trim() ?? '',
    interestPhoneNumber: formatPhoneNumber(user?.phoneNumber ?? ''),
    isInterestExpanded: false,
  };
}

function saveInterestDraftSnapshot(companyId: string, state: InterestViewState) {
  if (state.hasSavedInterest) {
    clearCompanyActivationInterestDraft(companyId);
    return;
  }

  if (
    !state.isInterestExpanded &&
    state.interestName.trim().length === 0 &&
    state.interestPhoneNumber.trim().length === 0
  ) {
    clearCompanyActivationInterestDraft(companyId);
    return;
  }

  saveCompanyActivationInterestDraft({
    companyId,
    customerName: state.interestName,
    customerPhoneNumber: state.interestPhoneNumber,
    isExpanded: state.isInterestExpanded,
  });
}

export function CompanyInactiveState({ company, fullAddress, whatsappUrl }: CompanyInactiveStateProps) {
  const { user } = useAuth();
  const interestSectionRef = useRef<HTMLElement | null>(null);
  const phoneMaskRef = useMaskInput({ mask: ['(99) 9999-9999', '(99) 99999-9999'] });
  const [interestState, setInterestState] = useState<InterestViewState>(() => buildInterestViewState(company.id, user));
  const { hasSavedInterest, interestName, interestPhoneNumber, isInterestExpanded } = interestState;
  const googleMapsUrl = buildGoogleMapsUrl({
    address: fullAddress,
    latitude: company.latitude,
    longitude: company.longitude,
  });

  const updateInterestState = (getNextState: (currentState: InterestViewState) => InterestViewState) => {
    setInterestState((currentState) => {
      const nextState = getNextState(currentState);

      saveInterestDraftSnapshot(company.id, nextState);

      return nextState;
    });
  };

  const handleOpenInterest = () => {
    updateInterestState((currentState) => ({
      ...currentState,
      isInterestExpanded: true,
    }));

    window.requestAnimationFrame(() => {
      interestSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };
  const isInterestFormReady = interestName.trim().length > 0 && isPhoneNumberComplete(interestPhoneNumber);
  const companyName = company.name.trim();
  const whatsappButtonLabel = companyName
    ? `Falar com ${companyName} no WhatsApp`
    : 'Falar com a barbearia no WhatsApp';

  const handleInterestSubmit = () => {
    try {
      const savedInterest = saveCompanyActivationInterest({
        companyId: company.id,
        companyName: company.name,
        customerName: interestName.trim(),
        customerPhoneNumber: interestPhoneNumber,
      });

      console.info('[CompanyInactiveState] Activation interest saved', savedInterest);
      setInterestState((currentState) => ({
        ...currentState,
        hasSavedInterest: true,
        isInterestExpanded: true,
      }));
      clearCompanyActivationInterestDraft(company.id);
      toast.success('Pronto. Vamos te avisar quando este lugar começar a receber agendamentos no Vira Tempo.');
    } catch {
      toast.error('Não foi possível salvar seu interesse agora. Tente novamente em instantes.');
    }
  };

  return (
    <div className="bg-background min-h-screen pb-10">
      <Header showBack />

      <div className="relative h-[20rem] overflow-hidden md:h-[22rem] lg:h-[24rem]">
        <img src={resolveCompanyBanner(company.coverPhoto)} alt={company.name} className="h-full w-full object-cover" />
        <div className="from-foreground/80 via-foreground/40 to-foreground/10 absolute inset-0 bg-gradient-to-t" />
        <div className="absolute right-0 bottom-0 left-0 p-6 pb-12 md:p-6 md:pb-14">
          <div className="container">
            <span className="bg-warning text-warning-foreground inline-flex rounded-full px-3 py-1 text-xs font-semibold">
              Em breve no Vira Tempo
            </span>
            <h1 className="text-background mt-3 text-2xl font-bold md:text-3xl">{company.name}</h1>
          </div>
        </div>
      </div>

      <div className="relative container -mt-10 pb-10">
        <div className="mx-auto grid max-w-6xl gap-5 lg:grid-cols-[1.35fr_0.95fr]">
          <section className="border-border bg-card rounded-[1.75rem] border p-6 shadow-xl md:p-8">
            <h2 className="text-card-foreground text-2xl leading-tight font-bold md:text-4xl">
              Este lugar ainda não aceita agendamentos online
            </h2>
            <p className="text-muted-foreground mt-3 max-w-2xl text-sm md:text-base">
              Ainda não dá pra agendar por aqui. Enquanto isso, você pode falar direto com a empresa ou pedir pra ser
              avisado quando a agenda abrir no Vira Tempo.
            </p>

            <div className="mt-6 flex flex-col gap-3">
              {whatsappUrl ? (
                <Button
                  asChild
                  size="lg"
                  className="h-auto min-h-14 w-full bg-[hsl(142,70%,41%)] px-4 py-3 text-center text-base leading-snug font-semibold whitespace-normal hover:bg-[hsl(142,70%,37%)]"
                >
                  <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                    <FaWhatsapp className="h-4 w-4" />
                    {whatsappButtonLabel}
                  </a>
                </Button>
              ) : (
                <Button
                  size="lg"
                  className="h-auto min-h-14 w-full px-4 py-3 text-center text-base leading-snug font-semibold whitespace-normal"
                  disabled
                >
                  {whatsappButtonLabel}
                </Button>
              )}
              <p className="text-muted-foreground px-1 text-sm">
                {whatsappUrl ? 'Resposta direta com a empresa' : 'O contato desta empresa ainda não está disponível.'}
              </p>

              <Button
                type="button"
                size="lg"
                variant="outline"
                className="h-14 w-full text-center text-base font-semibold"
                onClick={handleOpenInterest}
                aria-expanded={isInterestExpanded || hasSavedInterest}
                aria-controls="activation-interest"
              >
                <Bell className="h-4 w-4" />
                Quero agendar aqui quando liberar
              </Button>
              <p className="text-muted-foreground px-1 text-sm">Seja um dos primeiros a agendar aqui</p>
            </div>
          </section>

          <aside className="space-y-4">
            <div className="border-border bg-card rounded-[1.5rem] border p-5 shadow-sm">
              <h3 className="text-card-foreground text-base font-semibold">Contato deste lugar</h3>
              <div className="mt-4 space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <p className="text-card-foreground text-sm font-medium">Endereço</p>
                    <p className="text-muted-foreground mt-1 text-sm">{fullAddress || '—'}</p>
                    {googleMapsUrl ? (
                      <a
                        href={googleMapsUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary mt-2 inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
                      >
                        Ver no Google Maps
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    ) : null}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Phone className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <p className="text-card-foreground text-sm font-medium">Telefone</p>
                    <p className="text-muted-foreground mt-1 text-sm">
                      {formatPhoneNumber(company.phoneNumber) || 'O contato desta empresa ainda não está disponível.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-primary/20 bg-primary-light rounded-[1.5rem] border p-5 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="bg-background text-primary mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl shadow-sm">
                  <Store className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-card-foreground text-base font-semibold">
                    Você é o responsável por este negócio?
                  </h3>
                  <p className="text-muted-foreground mt-1 text-sm">
                    Ative este perfil no Vira Tempo e comece a receber agendamentos online.
                  </p>
                </div>
              </div>

              <Button asChild variant="outline" className="bg-background mt-4 w-full">
                <a
                  href={COMPANY_CLAIM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Quero ativar este negócio no Vira Tempo"
                >
                  Sou o responsável por este negócio
                </a>
              </Button>
            </div>
          </aside>
        </div>

        {(isInterestExpanded || hasSavedInterest) && (
          <section
            ref={interestSectionRef}
            id="activation-interest"
            className="border-border bg-card mx-auto mt-5 max-w-6xl rounded-[1.75rem] border p-6 shadow-sm md:p-8"
          >
            <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">
              <div>
                <div className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-2xl">
                  <BellRing className="text-primary h-5 w-5" />
                </div>
                <h3 className="text-card-foreground mt-4 text-xl font-bold md:text-2xl">
                  Quer agendar aqui? A gente te avisa quando liberar.
                </h3>
                <p className="text-muted-foreground mt-2 text-sm md:text-base">
                  Deixe seu contato e marque seu interesse. Quando este lugar começar a receber agendamentos no Vira
                  Tempo, você pode ser um dos primeiros a saber.
                </p>

                <div className="border-primary/15 bg-primary-light mt-5 rounded-2xl border px-4 py-3">
                  <p className="text-card-foreground text-sm font-medium">Isto é um aviso de ativação.</p>
                  <p className="text-muted-foreground mt-1 text-sm">
                    Ainda não existe agenda aberta. Você não está entrando em fila de horários.
                  </p>
                </div>
              </div>

              <div className="border-border bg-background rounded-[1.5rem] border p-5">
                {hasSavedInterest ? (
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="bg-success text-success-foreground flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl">
                        <CheckCircle2 className="h-5 w-5" />
                      </div>
                      <p className="text-card-foreground text-base font-semibold">
                        Pronto. Vamos te avisar quando este lugar começar a receber agendamentos no Vira Tempo.
                      </p>
                    </div>

                    <div className="border-border bg-card space-y-3 rounded-2xl border p-4">
                      <div>
                        <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">Seu nome</p>
                        <p className="text-card-foreground mt-1 text-sm font-medium">{interestName}</p>
                      </div>

                      <div>
                        <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                          Seu WhatsApp
                        </p>
                        <p className="text-card-foreground mt-1 text-sm font-medium">{interestPhoneNumber}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="activation-interest-name">Seu nome</Label>
                      <Input
                        id="activation-interest-name"
                        value={interestName}
                        onChange={(event) =>
                          updateInterestState((currentState) => ({
                            ...currentState,
                            interestName: event.target.value,
                          }))
                        }
                        placeholder="Como podemos te chamar?"
                        className="bg-muted h-12 rounded-xl border-none"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="activation-interest-phone">Seu WhatsApp</Label>
                      <Input
                        id="activation-interest-phone"
                        type="tel"
                        value={interestPhoneNumber}
                        onChange={(event) =>
                          updateInterestState((currentState) => ({
                            ...currentState,
                            interestPhoneNumber: event.target.value,
                          }))
                        }
                        placeholder="(11) 99999-0000"
                        className="bg-muted h-12 rounded-xl border-none"
                        ref={phoneMaskRef}
                      />
                    </div>

                    <Button
                      type="button"
                      className="h-12 w-full text-sm font-semibold"
                      onClick={handleInterestSubmit}
                      disabled={!isInterestFormReady}
                    >
                      Quero ser avisado
                    </Button>

                    <p className="text-muted-foreground text-xs leading-relaxed">
                      A gente usa esse contato apenas para avisar quando este lugar começar a receber agendamentos pelo
                      Vira Tempo.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
